// Inline dimmable-light card for the room grid — compact vertical channel
// sliders, always visible, sized to sit next to the touch/fan cards. Covers all
// three variants (DeviceID 25 W/WH, 24 R/G/B, 23 W/WW/R/G/B). Publishes the
// comma-joined channel values on release; seeds its last-known status from the
// DB (like FanDial) then tracks live MQTT.

import React, { useEffect, useState } from 'react';
import { Card, Box, Typography, Slider, Button } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { useApp } from '../../../core/state/AppContext';
import { useDeviceStatus } from '../../../core/mqtt/useDeviceStatus';
import { parseStatus } from '../../../core/mqtt/mqttClient';
import { publishCommand } from '../../../core/utils/deviceController';
import { deviceTypeKey } from '../../../core/devices/deviceMeta';
import { getDeviceStatus } from '../../../core/api/api';
import DeviceStatusDot from './DeviceStatusDot';
import DeviceCardMenu from './DeviceCardMenu';

const CHANNELS = {
  rgb: ['R', 'G', 'B'],
  wwh: ['W', 'WH'],
  wwrgb: ['W', 'WW', 'R', 'G', 'B'],
};

function channelColor(ch) {
  const c = String(ch).toLowerCase();
  if (c === 'r') return '#e53935';
  if (c === 'g') return '#43a047';
  if (c === 'b') return '#1e88e5';
  if (c === 'w') return '#e0e0e0';
  if (c === 'ww' || c === 'wh') return '#ffb300';
  return '#90caf9';
}

export default function LightControlCard({ device, subtitle }) {
  const { userId } = useApp();
  const uno = device.UniqueNumber;
  const channels = CHANNELS[deviceTypeKey(device.DeviceID)] || ['R', 'G', 'B'];
  // Seed from the already-loaded device row (list includes `Status`) so the card
  // reflects the real state at once; live MQTT then keeps it current.
  const [status, setStatus, liveAt] = useDeviceStatus(uno, parseStatus(device.Status || device.DeviceStatus || ''));
  const [values, setValues] = useState(() => channels.map(() => 0));

  useEffect(() => {
    let alive = true;
    (async () => {
      const st = await getDeviceStatus(uno).catch(() => null);
      if (!alive) return;
      const row = st && Array.isArray(st.data) ? st.data[0] : null;
      if (row && row.Status) {
        const db = parseStatus(row.Status);
        if (db) setStatus((cur) => cur || db);
      }
    })();
    return () => { alive = false; };
  }, [uno, setStatus]);

  useEffect(() => {
    if (!status) return;
    const parts = status.split(',').map((n) => Number(n));
    if (parts.length >= channels.length && parts.every((n) => !Number.isNaN(n))) {
      setValues(parts.slice(0, channels.length));
    }
  }, [status, channels.length]);

  const onChange = (i, v) => setValues((prev) => { const u = [...prev]; u[i] = v; return u; });
  const commit = (i, v) => { const u = [...values]; u[i] = v; publishCommand(uno, u.join(','), userId); };
  const allOff = () => { const z = channels.map(() => 0); setValues(z); publishCommand(uno, z.join(','), userId); };
  const isOn = values.some((v) => v > 0);

  return (
    <Card sx={{ p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>{subtitle}</Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap>{device.DeviceName || uno}</Typography>
        </Box>
        <DeviceStatusDot device={device} live={liveAt} />
        <DeviceCardMenu device={device} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-around', alignItems: 'flex-end', flex: 1, pt: 0.5 }}>
        {channels.map((ch, i) => (
          <Box key={ch} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
            <Slider
              orientation="vertical"
              min={0} max={255} value={values[i]}
              onChange={(_, v) => onChange(i, v)}
              onChangeCommitted={(_, v) => commit(i, v)}
              sx={{ height: 88, color: channelColor(ch), '& .MuiSlider-rail': { opacity: 0.25 }, '& .MuiSlider-thumb': { width: 13, height: 13 } }}
            />
            <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ lineHeight: 1 }}>{ch}</Typography>
          </Box>
        ))}
      </Box>

      <Button
        size="small" fullWidth variant={isOn ? 'contained' : 'outlined'}
        color={isOn ? 'error' : 'inherit'}
        startIcon={<PowerSettingsNewIcon />} onClick={allOff}
        sx={{ mt: 0.5, color: isOn ? undefined : 'text.primary', borderColor: isOn ? undefined : 'divider' }}
      >
        Off
      </Button>
    </Card>
  );
}
